<script setup lang="ts">
/**
 * CSP Sync 모달 — Change Profile 팝업의 CSP Sync 버튼에서 연다.
 * (SgUenBatchSyncModal.vue 복사 후 CSP Sync 용으로 바꿈 — 원본은 무수정)
 *
 * 흐름
 * ────
 *  1) CSP UEN 여러 개 입력
 *  2) [Run] → POST /api/cspsync/check — 이미 이 원래 회사에 등록된 UEN 구분
 *     - 전부 등록돼 있으면 바로 결과 표시(로그인 없음)
 *  3) 남은 게 있으면 SgPyLoginDialog(Singpass 로그인) → 그 sessionId 로
 *     POST /api/cspsync/import → SG 데몬 CSP 콤보 목록 대조 → L_MULTI_CSP 추가
 *  4) 결과 표 (Added / Already registered / No access / Failed)
 */
import { ref, computed, watch } from 'vue';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { useTranslation } from '@/utils/locales/messages';
import SgPyLoginDialog from '@/features/startTask/preset_task/sg/components/change-financial-year-end/SgPyLoginDialog.vue';
import { cspSyncApi, type CspSyncResultRow, type CspSyncImportResult, type CspSyncStatus } from '../api/cspSyncApi';

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [v: boolean];
  synced: [];
}>();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);

type CspSyncSummary = Omit<CspSyncImportResult, 'results'>;

const rawText = ref('');
const busy = ref(false);
const errorMsg = ref('');
const results = ref<CspSyncResultRow[]>([]);
const summary = ref<CspSyncSummary | null>(null);
// 로그인 후 보낼 입력 전체 — 서버가 다시 등록 여부를 확인함
const pendingUens = ref<string[]>([]);

// Singpass 로그인 세션 — 매번 새로 로그인(1회용)
const activeSessionId = ref<string>('');
const showLoginDialog = ref(false);

// Filing Agent UEN — SgPyLoginDialog 이 인증 완료 요청에 반드시 필요. 현재 선택한 프로필(세션)의 regNo.
const sessionStore = useSessionStore();
function resolveSessionRegNo(): string {
  try {
    return String(sessionStore.getCustomerInfo()?.regNo || '').replace(/\s/g, '').trim();
  } catch { return ''; }
}
const activeFilingAgentUEN = computed(() => (resolveSessionRegNo() || '').toUpperCase());

const parsedUens = computed<string[]>(() => {
  const src = rawText.value || '';
  const raw = src.split(/[\s,;]+/).map((s) => s.trim().toUpperCase()).filter(Boolean);
  return Array.from(new Set(raw));
});

// 결과가 이미 화면에 있으면 Run 을 잠근다 — Reset 후 다시 실행.
const hasResults = computed(() => results.value.length > 0 || summary.value !== null);
const canRun = computed(
  () => !busy.value && parsedUens.value.length > 0 && !hasResults.value,
);

const summaryType = computed(() => {
  const s = summary.value;
  if (!s) return 'info';
  const bad = s.failed + s.noAccess;
  if (bad === 0) return 'success';
  if (s.added === 0) return 'error';
  return 'info';
});

const STATUS_COLOR: Record<CspSyncStatus, string> = {
  added: 'success',
  already_registered: 'info',
  no_access: 'warning',
  failed: 'error',
};

function statusLabel(status: CspSyncStatus): string {
  switch (status) {
    case 'added': return t('entity.CspSyncAdded') || 'Added';
    case 'already_registered': return t('entity.CspSyncAlreadyRegistered') || 'Already registered';
    case 'no_access': return t('entity.CspSyncNoAccess') || 'No access';
    default: return t('entity.CspSyncFailed') || 'Failed';
  }
}

/** 모달 상태 초기화 — 열릴 때마다 이전 입력/결과 지운다. */
function resetState() {
  rawText.value = '';
  errorMsg.value = '';
  results.value = [];
  summary.value = null;
  activeSessionId.value = '';
  pendingUens.value = [];
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) resetState();
  },
);

function close() {
  if (busy.value) return;
  emit('update:modelValue', false);
}

/** [Run] — 먼저 등록 여부 확인, 남은 게 있으면 Singpass 로그인(매번 새로). */
async function onRunClick() {
  if (!canRun.value) return;
  errorMsg.value = '';
  busy.value = true;
  try {
    const chk = await cspSyncApi.check(parsedUens.value);
    if (!chk.remaining || chk.remaining.length === 0) {
      results.value = (chk.registered || []).map((u) => ({
        uen: u, status: 'already_registered' as const, error: 'Already registered.',
      }));
      summary.value = { total: chk.total, added: 0, alreadyRegistered: chk.total, noAccess: 0, failed: 0 };
      return;
    }
    // 로그인 다이얼로그가 filingAgentUEN 을 요구함 — 미리 검증해서 로그인 후 튕기지 않도록.
    if (!activeFilingAgentUEN.value.trim()) {
      errorMsg.value = t('entity.CspSyncNoFilingAgent') || 'Company UEN is missing. Please check your company settings.';
      return;
    }
    pendingUens.value = [...parsedUens.value];
    activeSessionId.value = '';
    showLoginDialog.value = true;
  } catch {
    // 서버는 공통 문구 객체만 줌 → 자체 문구
    errorMsg.value = t('entity.CspSyncError') || 'CSP sync failed. Please try again.';
  } finally {
    busy.value = false;
  }
}

/** 로그인 다이얼로그 → authenticated 이벤트. 로그인 세션 저장 후 즉시 실행. */
function onAuthenticated(payload: { sessionId: string; mstIdx: number | null; filingAgentUEN: string }) {
  showLoginDialog.value = false;
  activeSessionId.value = payload.sessionId;
  void executeImport();
}

async function executeImport() {
  if (busy.value) return;
  errorMsg.value = '';
  busy.value = true;
  try {
    const r = await cspSyncApi.import(activeSessionId.value, pendingUens.value);
    results.value = r.results || [];
    summary.value = {
      total: Number(r.total || 0),
      added: Number(r.added || 0),
      alreadyRegistered: Number(r.alreadyRegistered || 0),
      noAccess: Number(r.noAccess || 0),
      failed: Number(r.failed || 0),
    };
    emit('synced');
  } catch {
    errorMsg.value = t('entity.CspSyncError') || 'CSP sync failed. Please try again.';
  } finally {
    busy.value = false;
    // 세션 재활용 금지 — 한 번 쓴 세션은 항상 폐기.
    activeSessionId.value = '';
  }
}
</script>

<template>
  <v-dialog :model-value="props.modelValue" :max-width="760" persistent @update:model-value="close">
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span>{{ t('entity.CspSyncTitle') || 'CSP Sync · Add CSPs by UEN(s)' }}</span>
        <v-btn icon="mdi-close" variant="text" size="small" :disabled="busy" @click="close" />
      </v-card-title>

      <v-card-text>
        <p class="text-body-2 text-medium-emphasis mb-2">
          {{ t('entity.CspSyncDescription') || 'Enter the UENs of CSPs available to your Singpass account. UENs already registered are skipped; the rest are checked after Singpass login.' }}
        </p>

        <v-textarea
          v-model="rawText"
          label="CSP UEN"
          rows="6"
          auto-grow
          hide-details
          class="mb-2"
          placeholder="e.g. 202546698G, 202313794G"
          :disabled="busy"
        />

        <div class="d-flex align-center mb-3">
          <div class="ml-auto text-body-2 text-medium-emphasis">
            {{ parsedUens.length }} UEN(s)
          </div>
        </div>

        <v-alert v-if="errorMsg" type="error" density="compact" class="mb-3" variant="tonal">
          {{ errorMsg }}
        </v-alert>

        <v-alert v-if="summary" density="compact" class="mb-3" variant="tonal" :type="summaryType">
          {{ t('entity.CspSyncTotal') || 'Total' }} {{ summary.total }}
          · {{ t('entity.CspSyncAdded') || 'Added' }} {{ summary.added }}
          · {{ t('entity.CspSyncAlreadyRegistered') || 'Already registered' }} {{ summary.alreadyRegistered }}
          · {{ t('entity.CspSyncNoAccess') || 'No access' }} {{ summary.noAccess }}
          · {{ t('entity.CspSyncFailed') || 'Failed' }} {{ summary.failed }}
        </v-alert>

        <v-table v-if="results.length" density="compact" class="results-table">
          <thead>
            <tr>
              <th>UEN</th>
              <th>{{ t('entity.CspSyncCompanyName') || 'Company Name' }}</th>
              <th>{{ t('entity.CspSyncStatus') || 'Status' }}</th>
              <th>{{ t('entity.CspSyncReason') || 'Reason' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in results" :key="row.uen">
              <td>{{ row.uen }}</td>
              <td>{{ row.name || '—' }}</td>
              <td>
                <v-chip size="small" :color="STATUS_COLOR[row.status] || 'error'" variant="tonal">
                  {{ statusLabel(row.status) }}
                </v-chip>
              </td>
              <td class="text-caption text-medium-emphasis">
                <span v-if="row.status !== 'added' && row.error">{{ row.error }}</span>
                <span v-else>—</span>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn variant="text" :disabled="busy" @click="close">Close</v-btn>
        <v-btn
          v-if="hasResults"
          variant="tonal"
          :disabled="busy"
          prepend-icon="mdi-refresh"
          @click="resetState"
        >
          {{ t('entity.Reset') || 'Reset' }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          :loading="busy"
          :disabled="!canRun"
          prepend-icon="mdi-account-multiple-plus"
          @click="onRunClick"
        >
          {{ t('entity.CspSyncRun') || 'Run' }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Singpass 로그인 다이얼로그 (개별싱크와 같은 컴포넌트 재사용) -->
    <SgPyLoginDialog
      v-model="showLoginDialog"
      :filing-agent-u-e-n="activeFilingAgentUEN"
      @authenticated="onAuthenticated"
    />
  </v-dialog>
</template>

<style scoped>
.results-table :deep(th) {
  background: rgba(0, 0, 0, 0.04);
}
</style>
